import React, { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { useProductAssets, Direction } from '../../../hooks/useProductAssets';
import { NavCategorySet } from '../../../models/NavCategorySet.model';
import { ViewProduct } from '../../../models/ViewProduct.model';
import { blankImage } from '../../../product_detail/components/ImageLoader';
import { LazyImageLoader } from '../../../product_detail/components/LazyImageLoader';
import { collectionSelectedVariantFamily } from '../../../recoil/atoms';
import { Link } from '../../../root/components/Link';
import { sizeImage } from '../../../util';
import styles from './DesktopHeader.module.scss';

export const DesktopHeaderNavColumn: React.FC<{
  category: NavCategorySet;
  availableProducts: ViewProduct[];
  hideProductImage?: boolean;
  numberOfProducts?: number;
  onActiveProductChange?: (product?: ViewProduct) => void;
  inlineStyle?: React.CSSProperties;
}> = ({ availableProducts, category, hideProductImage, numberOfProducts = 4, onActiveProductChange, inlineStyle }) => {
  const [activeProduct, setActiveProduct] = useState<ViewProduct>(availableProducts[0]);
  const photos = useProductAssets(activeProduct);
  const [selectedVariant] = useRecoilState(collectionSelectedVariantFamily(activeProduct.handle));

  const filteredMannequinPhotos = photos.mannequinPhotos.filter(({ color, direction }) => color === selectedVariant.color && direction === Direction.Front);
  const filteredModelPhotos = photos.modelPhotos.filter(({ color }) => color === selectedVariant.color);
  const mainPhoto = filteredMannequinPhotos[0] ?? filteredModelPhotos[0] ?? { src: blankImage };
  const mainPhotoSrc = sizeImage(mainPhoto.src, '350x443');

  const hoverPhoto = filteredModelPhotos[0] ?? filteredMannequinPhotos[0] ?? { src: blankImage };

  const [isHovering, setIsHovering] = useState<boolean>(false);
  const hoverPhotoSrc = sizeImage(hoverPhoto.src, '350x443');

  const [activeSrc, setActiveSrc] = useState<string>(mainPhotoSrc);

  const setProductAndHover = (product: ViewProduct, hovering: boolean) => {
    setHoverProductHandleDebounce(product, hovering);
  };

  let debounceTimeout: ReturnType<typeof setTimeout>;
  const setHoverProductHandleDebounce = (product: ViewProduct | undefined, hovering: boolean) => {
    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => {
      setIsHovering(hovering);
      product && setActiveProduct(product);
    }, 200);
  };

  const onMouseLeave = () => {
    clearTimeout(debounceTimeout);
  };

  useEffect(() => {
    setActiveSrc(isHovering ? hoverPhotoSrc : mainPhotoSrc);
    onActiveProductChange?.(isHovering ? activeProduct : undefined);
  }, [activeProduct, hoverPhotoSrc, isHovering, mainPhotoSrc]);

  return (
    <div className={styles.shopAllCategoryColumn} style={{ ...inlineStyle }}>
      <div
        className={styles.categoryTitle}
        onMouseEnter={(): void => {
          setProductAndHover(availableProducts[0], false);
        }}
      >
        <Link
          dataTrackLabel="Desktop Navigation"
          dataTrackLocation="Desktop Navigation"
          href={category.link}
          labelStyleOptions={{ uppercase: true }}
          text={`${category.title} →`}
        />
      </div>
      <div onBlur={() => void 0} onFocus={() => void 0}>
        {availableProducts
          .filter((product) => product.tags.some((c) => category.productTags.includes(c)))
          .slice(0, numberOfProducts)
          .map((product) => {
            return (
              <div
                key={product.id}
                className={styles.categoryProductTitle}
                onMouseEnter={(): void => {
                  setProductAndHover(product, true);
                }}
                onBlur={() => void 0}
                onFocus={() => void 0}
                onMouseLeave={onMouseLeave}
              >
                <Link dataTrackLabel="Desktop Navigation" dataTrackLocation="Desktop Navigation" href={`/products/${product.handle}`} text={product.title} />
              </div>
            );
          })}
        <div className={styles.allCategoryLink}>
          <Link
            dataTrackLabel="Desktop Navigation"
            dataTrackLocation="Desktop Navigation"
            href={category.link}
            labelStyleOptions={{ color: '#6065e0' }}
            text={`All ${category.title}`}
          />
        </div>
      </div>
      <div style={{ flex: 1 }}></div>
      {!hideProductImage && (
        <div
          className={styles.columnImageWrapper}
          onMouseEnter={(): void => {
            setIsHovering(true);
          }}
          onBlur={() => void 0}
          onFocus={() => void 0}
          onMouseLeave={() => {
            setIsHovering(false);
            onMouseLeave();
          }}
        >
          <Link dataTrackLabel="Desktop Navigation" dataTrackLocation="Desktop Navigation" href={`/products/${activeProduct.handle}`} buttonType="ghost">
            <LazyImageLoader src={activeSrc} alt={`${activeProduct.title} shown`} />
            <div className={styles.activeTitle}>{activeProduct.title}</div>
          </Link>
        </div>
      )}
    </div>
  );
};

export default DesktopHeaderNavColumn;
